import React from 'react'

import { cn } from './cn'

// DOM mirrors Semantic UI React's Table (collections/Table/*.js): table, thead, tbody,
// tr, th and td, with no marker classes (no Semantic CSS behind them). Stacking below
// 767px is opt-in via `stackable`; the rules live in globals.css under `ui-table-stackable`
// because they are a media query over several descendant selectors (see README).

export type TableProps = React.TableHTMLAttributes<HTMLTableElement> & {
  celled?: boolean
  collapsing?: boolean
  stackable?: boolean
}

export type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  verticalAlign?: 'top'
}

const CELLED_CELLS =
  '[&_tr_th]:border-l [&_tr_td]:border-l [&_tr_th]:border-l-[rgba(34,36,38,.1)] [&_tr_td]:border-l-[rgba(34,36,38,.1)] [&_tr_th:first-child]:border-l-0 [&_tr_td:first-child]:border-l-0'

// `.ui.table tr td` top border, with none on the first row (`tr:first-child td`).
const ROW_BORDERS =
  '[&_tr_td]:border-t [&_tr_td]:border-t-[rgba(34,36,38,.1)] [&_tr:first-child_td]:border-t-0'

function TableRoot({
  celled,
  children,
  className,
  collapsing,
  stackable,
  ...rest
}: TableProps) {
  const classes = cn(
    collapsing ? 'w-auto' : 'w-full',
    'bg-white my-[1em] border border-solid border-[rgba(34,36,38,.15)] rounded-[.28571429rem] text-left text-[rgba(0,0,0,.87)] border-separate border-spacing-0 first:mt-0 last:mb-0',
    ROW_BORDERS,
    celled && CELLED_CELLS,
    stackable && 'ui-table-stackable',
    className,
  )

  return (
    <table {...rest} className={classes}>
      {children}
    </table>
  )
}

function TableHeader({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead {...rest} className={cn(className)}>
      {children}
    </thead>
  )
}

function TableBody({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody {...rest} className={cn(className)}>
      {children}
    </tbody>
  )
}

function TableRow({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr {...rest} className={cn(className)}>
      {children}
    </tr>
  )
}

// `.ui.table thead th`, plus the rounded corners on the first header row.
const HEADER_CELL =
  'border-0 border-solid bg-[#f9fafb] py-[.92857143em] px-[.78571429em] text-left font-bold text-[rgba(0,0,0,.87)] align-[inherit] border-b border-b-[rgba(34,36,38,.1)] cursor-auto [tr:first-child>&:first-child]:rounded-tl-[.28571429rem] [tr:first-child>&:last-child]:rounded-tr-[.28571429rem] [tr:first-child>&:only-child]:rounded-t-[.28571429rem]'

function TableHeaderCell({
  children,
  className,
  ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th {...rest} className={cn(HEADER_CELL, className)}>
      {children}
    </th>
  )
}

// `.ui.table td`; `verticalAlign="top"` is the `top aligned` rule.
const CELL = 'border-0 border-solid p-[.78571429em] text-left'
const CELL_VERTICAL_ALIGN: Record<'top', string> = {
  top: 'align-top',
}

function TableCell({
  children,
  className,
  verticalAlign,
  ...rest
}: TableCellProps) {
  const classes = cn(
    CELL,
    verticalAlign && CELL_VERTICAL_ALIGN[verticalAlign],
    className,
  )

  return (
    <td {...rest} className={classes}>
      {children}
    </td>
  )
}

export const Table = Object.assign(TableRoot, {
  Body: TableBody,
  Cell: TableCell,
  Header: TableHeader,
  HeaderCell: TableHeaderCell,
  Row: TableRow,
})
